// components/AdminTopbar.jsx
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ChevronDown, Search, X, Menu, User, LogOut, Shield } from "lucide-react";
import { useAuth } from "../Auth-Context/AuthContext";

export default function AdminTopbar({ title, subtitle, actions = [], search, onMenuClick }) {
  const navigate         = useNavigate();
  const { user, logout } = useAuth();
  const [dropdownOpen,   setDropdownOpen] = useState(false);
  const dropdownRef      = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const userDisplayName = user?.name ||
    (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : "Admin");
  const userRole =
    user?.role === "SUPER_ADMIN" ? "Super Admin" :
    user?.role === "ADMIN"       ? "Admin"       : "Manager";
  const userInitials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    : (user?.email ? user.email.substring(0, 2).toUpperCase() : "AD");

  const handleProfile = () => { setDropdownOpen(false); navigate("/profile"); };
  const handleLogout  = async () => {
    setDropdownOpen(false);
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <header
      className="flex items-center flex-shrink-0 gap-2 px-3 bg-white border-b md:gap-3 border-neutral-100 md:px-7"
      style={{ height: "56px", position: "sticky", top: 0, zIndex: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}
    >
      {/* Mobile hamburger — passed from AdminLayout */}
      {onMenuClick && (
        <button
          onClick={onMenuClick}
          className="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-lg md:hidden text-neutral-600 hover:bg-neutral-50"
        >
          <Menu size={18} />
        </button>
      )}

      <div className="flex flex-col justify-center flex-shrink min-w-0 pl-9 md:pl-0">
        <span className="text-sm font-bold truncate md:text-[15px] text-neutral-900 tracking-[-0.3px] leading-none">{title}</span>
        {subtitle && (
          <span className="hidden text-[10px] truncate md:inline-block md:text-[11px] text-neutral-400 mt-0.5 leading-none">{subtitle}</span>
        )}
      </div>

      <div className="flex-1" />

      {search && (
        <div className="items-center hidden h-8 gap-2 px-3 border rounded-lg sm:flex bg-neutral-50 border-neutral-100 w-36 md:w-52">
          <Search size={12} className="flex-shrink-0 text-neutral-400" />
          <input
            type="text" value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            placeholder={search.placeholder || "Search…"}
            className="w-full text-xs bg-transparent border-none outline-none text-neutral-700 placeholder-neutral-300"
          />
          {search.value && (
            <button onClick={() => search.onChange("")} className="flex-shrink-0 text-neutral-400 hover:text-neutral-600">
              <X size={11} />
            </button>
          )}
        </div>
      )}

      <div className="flex items-center gap-1 md:gap-2">
        {actions.map((action, i) => (
          <button key={i} onClick={action.onClick}
            className="flex items-center gap-1.5 px-2 md:px-3.5 h-8 rounded-lg text-xs font-semibold transition-colors"
            style={action.variant === "secondary"
              ? { background: "#f5f5f5", color: "#404040", border: "1px solid #e5e5e5" }
              : { background: "#009DB9", color: "#fff" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = action.variant === "secondary" ? "#ebebeb" : "#007a90"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = action.variant === "secondary" ? "#f5f5f5" : "#009DB9"; }}
          >
            {action.icon && <action.icon size={13} />}
            <span className="hidden sm:inline">{action.label}</span>
          </button>
        ))}
      </div>

      <button className="relative flex items-center justify-center flex-shrink-0 w-8 h-8 transition-colors rounded-lg text-neutral-400 hover:bg-neutral-50">
        <Bell size={16} />
        <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-red-500" />
      </button>

      {/* Avatar + dropdown */}
      <div className="relative flex-shrink-0" ref={dropdownRef}>
        <button
          onClick={() => setDropdownOpen((v) => !v)}
          className="flex items-center gap-1 pl-1 transition-colors border-l rounded-lg cursor-pointer md:gap-2 md:pl-2 border-neutral-100 hover:bg-neutral-50"
        >
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
            style={{ background: "#009DB9" }}
          >
            {userInitials}
          </div>
          <div className="flex-col hidden leading-none lg:flex">
            <span className="text-[12px] font-semibold text-neutral-800 truncate max-w-[100px]">{userDisplayName}</span>
            <span className="text-[10px] text-neutral-400">{userRole}</span>
          </div>
          <ChevronDown
            size={12}
            className="hidden transition-transform duration-200 sm:block text-neutral-400"
            style={{ transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }}
          />
        </button>

        {dropdownOpen && (
          <div
            className="absolute right-0 z-50 mt-2 overflow-hidden bg-white border top-full w-60 rounded-2xl border-neutral-100"
            style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}
          >
            {/* User header */}
            <div className="px-4 py-3.5 border-b border-neutral-100" style={{ background: "#fafafa" }}>
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white text-[13px] font-bold flex-shrink-0"
                  style={{ background: "linear-gradient(135deg,#009DB9,#007a90)" }}
                >
                  {userInitials}
                </div>
                <div className="min-w-0">
                  <div className="text-[13px] font-bold text-neutral-800 truncate">{userDisplayName}</div>
                  <div className="text-[11px] text-neutral-400 truncate">{user?.email}</div>
                  <div className="flex items-center gap-1 mt-1">
                    <Shield size={9} style={{ color: "#009DB9" }} />
                    <span className="text-[10px] font-semibold" style={{ color: "#009DB9" }}>{userRole}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Menu items */}
            <div className="py-1.5">
              <button
                onClick={handleProfile}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-left hover:bg-neutral-50 transition-colors group"
              >
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors group-hover:bg-[rgba(0,157,185,0.1)]" style={{ background: "rgba(0,157,185,0.06)" }}>
                  <User size={13} style={{ color: "#009DB9" }} />
                </div>
                <div>
                  <div className="text-[12px] font-semibold text-neutral-700">My Profile</div>
                  <div className="text-[10px] text-neutral-400">Account &amp; password settings</div>
                </div>
              </button>

              <div className="mx-4 my-1 border-t border-neutral-100" />

              <button
                onClick={handleLogout}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-left hover:bg-red-50 transition-colors group"
              >
                <div className="flex items-center justify-center flex-shrink-0 transition-colors rounded-lg w-7 h-7 bg-red-50 group-hover:bg-red-100">
                  <LogOut size={13} className="text-red-500" />
                </div>
                <div>
                  <div className="text-[12px] font-semibold text-red-500">Logout</div>
                  <div className="text-[10px] text-red-400">End your session</div>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}